import { Card } from "./ui/card";
import { Pencil, Check, X } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

import { CountdownTimer } from "./CountdownTimer";

interface StatValue {
  label: string;
  value: string | number;
  highlight?: boolean;
}

interface StatCardProps {
  label?: string;
  value?: string | number;
  highlight?: boolean;
  children?: React.ReactNode;
  secondaryLabel?: string;
  secondaryValue?: string | number;
  values?: StatValue[]; // For multiple stacked values
  onEdit?: () => void;
  isEditing?: boolean;
  editValue?: string;
  onEditValueChange?: (val: string) => void;
  onSave?: () => void;
  onCancel?: () => void;
}

function StatCard({ 
  label, value, highlight, children, secondaryLabel, secondaryValue, values, 
  onEdit, isEditing, editValue, onEditValueChange, onSave, onCancel 
}: StatCardProps) {
  return (
    <Card className="p-4 shadow-sm border-border/50 bg-card flex flex-col justify-between min-h-[90px] relative group">
      <div className="space-y-3">
        {isEditing ? (
          <div className="space-y-2">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{label}</p>
            <div className="flex items-center gap-1">
              <Input 
                value={editValue} 
                onChange={(e) => onEditValueChange?.(e.target.value)}
                className="h-8 text-sm font-bold"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSave?.();
                  if (e.key === "Escape") onCancel?.();
                }}
              />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600" onClick={onSave}>
                <Check className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={onCancel}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <>
            {values ? (
              values.map((v, i) => (
                <div key={i}>
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{v.label}</p>
                  <p className={`text-lg font-black leading-tight ${v.highlight ? "text-green-600" : ""}`}>{v.value}</p>
                </div>
              ))
            ) : (
              <>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{label}</p>
                    <p className={`text-xl font-black mt-1 ${highlight ? "text-green-600" : ""}`}>{value}</p>
                  </div>
                  {onEdit && (
                    <button 
                      onClick={onEdit}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-muted transition-all text-muted-foreground"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {(secondaryLabel || secondaryValue) && (
                  <div className="pt-2 border-t border-border/50">
                    <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">{secondaryLabel}</p>
                    <p className="text-sm font-bold text-foreground">{secondaryValue}</p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {children && <div className="mt-2">{children}</div>}
    </Card>
  );
}

interface AuctionStatsGridProps {
  currentBid: number;
  bidCount: number;
  startingBid: number;
  reservePrice?: number | null;
  status: string;
  endsAt?: string | null;
  spread: number;
  spreadLocked?: boolean;
  uniqueBidders: number;
  type?: "regular" | "flash" | null;
  durationMinutes?: number | null;
  onSpreadUpdate?: (val: number) => Promise<void>;
  onSpreadReset?: () => Promise<void>;
}

export function AuctionStatsGrid({
  currentBid,
  bidCount,
  startingBid,
  reservePrice,
  status,
  endsAt,
  spread,
  spreadLocked,
  uniqueBidders,
  type,
  durationMinutes,
  onSpreadUpdate,
  onSpreadReset,
}: AuctionStatsGridProps) {
  const [isEditingSpread, setIsEditingSpread] = useState(false);
  const [spreadValue, setSpreadValue] = useState(spread.toString());

  const handleSpreadSave = async () => {
    const val = parseInt(spreadValue, 10);
    if (isNaN(val) || val < 0) return;
    await onSpreadUpdate?.(val);
    setIsEditingSpread(false);
  };
  const isPreAuction = ["draft", "pending_review", "changes_requested", "approved"].includes(status);
  const isLive = ["live", "paused", "scheduled"].includes(status);
  const isPostAuction = !isPreAuction && !isLive;

  if (isPreAuction) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Startpris" value={`${startingBid.toLocaleString("nb-NO")} kr`} />
        {reservePrice && (
          <StatCard label="Minstepris" value={`${reservePrice.toLocaleString("nb-NO")} kr`} />
        )}
        {durationMinutes && (
          <StatCard 
            label="Varighet" 
            value={durationMinutes >= 1440 ? `${Math.round(durationMinutes / 1440)} dager` : `${Math.round(durationMinutes / 60)} timer`} 
            secondaryLabel="Type"
            secondaryValue={type === "flash" ? "Lynauksjon" : "Vanlig"}
          />
        )}
      </div>
    );
  }

  if (isLive) {
    const sellerBid = Math.max(0, currentBid - spread);
    const displayBid = bidCount > 0 ? currentBid : startingBid;
    const bidLabel = bidCount > 0 ? "Høyeste bud" : "Startpris";

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          values={[
            { label: bidLabel, value: `${displayBid.toLocaleString("nb-NO")} kr`, highlight: true },
            { label: "Til selger (netto)", value: bidCount > 0 ? `${sellerBid.toLocaleString("nb-NO")} kr` : "—" },
          ]}
        />
        <StatCard 
          values={[
            { label: "Antall bud", value: bidCount },
            { label: "Unike budgivere", value: uniqueBidders },
          ]}
        />
        <StatCard 
          label="Spread (gebyr)" 
          value={`${spread.toLocaleString("nb-NO")} kr`} 
          onEdit={onSpreadUpdate ? () => {
            setSpreadValue(spread.toString());
            setIsEditingSpread(true);
          } : undefined}
          isEditing={isEditingSpread}
          editValue={spreadValue}
          onEditValueChange={setSpreadValue}
          onSave={handleSpreadSave}
          onCancel={() => setIsEditingSpread(false)}
        >
          {spreadLocked && (
            <div className="mt-1 flex items-center gap-2">
              <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Overstyrt / Manuell spread</span>
              {onSpreadReset && (
                <button type="button" className="text-xs text-muted-foreground underline" onClick={() => { void onSpreadReset(); }}>
                  Tilbakestill til auto
                </button>
              )}
            </div>
          )}
        </StatCard>
        {endsAt && (
          <StatCard label="Avslutter" value="">
            <CountdownTimer endsAt={endsAt} />
          </StatCard>
        )}
      </div>
    );
  }

  // Post Auction
  const finalBid = currentBid;
  const sellerNet = Math.max(0, finalBid - spread);
  const bidLabel = bidCount > 0 ? "Vinnerbud" : "Startpris";
  const displayBid = bidCount > 0 ? finalBid : startingBid;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        values={[
          { label: bidLabel, value: `${displayBid.toLocaleString("nb-NO")} kr`, highlight: true },
          { label: "Utbetales selger", value: bidCount > 0 ? `${sellerNet.toLocaleString("nb-NO")} kr` : "—" },
        ]}
      />
      <StatCard label="Spread (gebyr)" value={`${spread.toLocaleString("nb-NO")} kr`} />
    </div>
  );
}
